import Alert from "@cloudscape-design/components/alert"
import Autosuggest from "@cloudscape-design/components/autosuggest"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import List from "@cloudscape-design/components/list"
import Modal from "@cloudscape-design/components/modal"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useState } from "react"

import type { CapabilitySet } from "@/services/capabilitySetsApi"
import type { GrantTarget } from "@/services/grantsApi"
import { useAuth } from "@/contexts/AuthContext"
import { useNotifications } from "@/contexts/NotificationContext"
import { useCreateGrant } from "@/hooks/queries/useGrants"
import { parseApiErrorMessage } from "@/utils/apiErrors"
import { isValidEmail, sameEmail } from "@/utils/email"

interface GrantAccessModalProps {
  target: GrantTarget
  /**
   * Modal title, e.g. "Grant access - STUDY-001".
   */
  header: string
  submitLabel: string
  /**
   * Capability sets the person can be given here.
   */
  sets: CapabilitySet[]
  /**
   * Emails suggested as the person types. Any typed email is accepted.
   */
  emailSuggestions?: string[]
  /**
   * Where the grant applies, shown under the capability set label.
   */
  note?: string
  onClose: () => void
}

/**
 * Grant a person one capability set on a study or DTA by email. Warns that the grant takes effect
 * only with a matching sign-in group.
 */
export default function GrantAccessModal({
  target,
  header,
  submitLabel,
  sets,
  emailSuggestions = [],
  note,
  onClose,
}: GrantAccessModalProps) {
  const { user } = useAuth()
  const { showToast } = useNotifications()
  const createGrant = useCreateGrant(target)
  const [email, setEmail] = useState("")
  const [setName, setSetName] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [setError, setSetError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const selected = sets.find((s) => s.name === setName)

  const handleSubmit = async () => {
    const trimmed = email.trim()
    const badEmail = !isValidEmail(trimmed)
      ? "Enter a valid email address"
      : sameEmail(trimmed, user?.email)
        ? "You can't grant yourself access"
        : null
    const noSet = selected ? null : "Select a capability set"
    setEmailError(badEmail)
    setSetError(noSet)
    setSubmitError(null)
    if (badEmail || noSet || !selected) return
    try {
      await createGrant.mutateAsync({ email: trimmed, capability_set: selected.name })
      showToast({
        subject: "Access granted",
        message: `${trimmed} now has ${selected.display_name}`,
        variant: "success",
      })
      onClose()
    } catch (err) {
      setSubmitError(parseApiErrorMessage(err, "Failed to grant access"))
    }
  }

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={header}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={createGrant.isPending}
              onClick={() => void handleSubmit()}
            >
              {submitLabel}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label="Person" errorText={emailError ?? undefined}>
          <Autosuggest
            value={email}
            onChange={({ detail }) => setEmail(detail.value)}
            options={emailSuggestions.map((value) => ({ value }))}
            enteredTextLabel={(value) => `Use "${value}"`}
            placeholder="Start typing an email"
            filteringType="auto"
            empty="No matching users"
            ariaLabel="Email of the person to grant access"
          />
        </FormField>
        <FormField label="Capability set" description={note} errorText={setError ?? undefined}>
          <Select
            selectedOption={
              selected ? { value: selected.name, label: selected.display_name } : null
            }
            onChange={({ detail }) => setSetName(detail.selectedOption.value ?? null)}
            options={sets.map((s) => ({
              value: s.name,
              label: s.display_name,
              description: s.description,
            }))}
            placeholder="Choose a capability set"
            empty="No capability sets can be granted here"
            ariaLabel="Capability set"
          />
        </FormField>
        {selected && (
          <SpaceBetween size="m">
            <Container header={<Header variant="h3">This lets them</Header>}>
              <List
                ariaLabel={`What ${selected.display_name} lets them do`}
                items={selected.capabilities}
                disablePaddings
                renderItem={(c) => ({
                  id: c.name,
                  icon: <Icon name="check" variant="success" />,
                  content: c.friendly_name,
                  secondaryContent: (
                    <Box fontSize="body-s" color="text-body-secondary">
                      {c.description}
                    </Box>
                  ),
                })}
              />
            </Container>
            {selected.groups.length > 0 ? (
              <Alert type="info">
                The grant takes effect only when the person signs in with a group mapped to{" "}
                {selected.display_name} ({selected.groups.join(", ")}). Group membership is managed
                outside CDR.
              </Alert>
            ) : (
              <Alert type="warning">
                No sign-in group maps to {selected.display_name}, so this grant gives no access
                until one does.
              </Alert>
            )}
          </SpaceBetween>
        )}
        {submitError && <Alert type="error">{submitError}</Alert>}
      </SpaceBetween>
    </Modal>
  )
}
